import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Loader2, MessageCircle, Pencil, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";
import WhatsAppOtpDialog from "@/components/vendor/WhatsAppOtpDialog";

export default function VendorWhatsAppCard({ vendor, onUpdated }) {
  const current = vendor.whatsapp_number || vendor.phone || "";
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const start = () => {
    if (!phone) { toast.error("Saisissez le nouveau numéro WhatsApp."); return; }
    if (!isValidPhoneNumber(phone)) { toast.error("Numéro invalide. Vérifiez l'indicatif et le nombre de chiffres."); return; }
    if (phone === current) { toast.error("C'est déjà votre numéro actuel."); return; }
    setOtpOpen(true);
  };

  const onVerified = async (token) => {
    setOtpOpen(false);
    setSaving(true);
    try {
      await api.post("/vendor/whatsapp", { phone, phone_verification_token: token });
      toast.success("Numéro WhatsApp mis à jour ✅ Vos prochaines commandes arriveront sur ce numéro.");
      setEditing(false);
      setPhone("");
      onUpdated?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-line bg-white p-6" data-testid="vendor-whatsapp-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><MessageCircle className="h-5 w-5" /></span>
          <div>
            <p className="font-display text-base font-bold text-ink">Numéro WhatsApp des notifications</p>
            <p className="mt-0.5 text-sm text-slate-500">Les alertes de commande et de paiement de votre boutique sont envoyées sur ce numéro.</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="font-mono text-base font-black text-ink" data-testid="vendor-whatsapp-current">{current || "Aucun numéro"}</span>
              {vendor.phone_verified && current ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700" data-testid="vendor-whatsapp-verified"><BadgeCheck className="h-3.5 w-3.5" /> Vérifié</span>
              ) : (
                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700" data-testid="vendor-whatsapp-unverified">Non vérifié</span>
              )}
            </div>
          </div>
        </div>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} data-testid="vendor-whatsapp-edit"
            className="flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand">
            <Pencil className="h-3.5 w-3.5" /> Changer de numéro
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-5 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/40 p-4" data-testid="vendor-whatsapp-form">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <PhoneField testId="vendor-whatsapp-new" label="Nouveau numéro WhatsApp Business" value={phone} onChange={setPhone} />
            <div className="flex gap-2">
              <button type="button" onClick={start} disabled={saving} data-testid="vendor-whatsapp-send-code"
                className="h-11 rounded-full bg-emerald-600 px-5 text-sm font-bold text-white disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Recevoir le code"}
              </button>
              <button type="button" onClick={() => { setEditing(false); setPhone(""); }} className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-slate-500 hover:text-rose-600" data-testid="vendor-whatsapp-cancel" aria-label="Annuler"><X className="h-4 w-4" /></button>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">Un code à 6 chiffres sera envoyé sur WhatsApp au nouveau numéro. Le changement n'est appliqué qu'après vérification.</p>
        </div>
      )}
      <WhatsAppOtpDialog open={otpOpen} phone={phone} onClose={() => setOtpOpen(false)} onVerified={onVerified} />
    </div>
  );
}
